import { graphql, Link } from 'gatsby';
import React, { useState } from 'react';
import { Column, Container, Row } from '../../components/Grid';
import Layout from '../../components/Layout';
import Section from '../../components/Section';
import Video from '../../components/Video';
import { getKeyedResources } from '../../utils/resource-helper';
import Resource from '../../components/Resource';
import Image from '../../components/Image';

const ClassroomResourcesPage = ({ data }) => {
  const {
    vocabularyMasterclass,
    taskCards,
    vocabularyTier1,
    vocabularyTier2,
    vocabularyTier3,
    vocabularyRtuTier1,
    vocabularyRtuTier2,
    vocabularyRtuTier3,
  } = getKeyedResources(data);
  const [isTabExpanded, setIsTabExpanded] = useState(false);

  return (
    <Layout
      title="Vocabulary"
      className="classroom-resources-subpage classroom-resources-subpage--vocabulary"
    >
      <Section>
        <Container>
          <div className="classroom-resources-subpage-header classroom-resources-subpage-header--vocabulary">
            <div className="classroom-resources-subpage-header-top-section">
              <Link
                to="/classroom-resources"
                className="classroom-resources-subpage-header-back-button"
              >
                <Image
                  className="classroom-resources-subpage-header-back-icon"
                  filename="back-button-icon.png"
                  alt="Back"
                />
                <span>Back to Learning Resources Overview</span>
              </Link>
              <div className="classroom-resources-subpage-header-resources-tab">
                <button
                  className={`classroom-resources-subpage-header-tab-button ${
                    isTabExpanded ? 'expanded' : ''
                  }`}
                  onClick={() => setIsTabExpanded(!isTabExpanded)}
                >
                  How to use these resources
                  <span className="caret-icon">▼</span>
                </button>
                {isTabExpanded && (
                  <div className="classroom-resources-subpage-header-tab-content">
                    <p>
                      <b>1. Start with the Explainer Video:</b> Watch the
                      Explainer video to understand the key concepts of each big
                      idea of literacy.
                      <br />
                      <b>2. Dive into the Microlearns:</b> Complete the 6–8
                      minute, self-paced modules for each big idea. These
                      modules provide research-based strategies and activities
                      you can immediately use with your students.
                      <br />
                      <b>3. See the Strategies in Action:</b> Watch the masterclass
                      video, where a teacher demonstrates these strategies in a
                      real-world classroom setting.
                      <br />
                      <b>4. Equip Yourself with Task Cards:</b> Print the task
                      cards to keep these strategies at your fingertips and use
                      the printable manipulatives to support hands-on learning
                      with students.
                      <br />
                      <b>5. Apply What You’ve Learned:</b> Use the ready-to-use
                      activity to teach foundational literacy skills like
                      phonics, vocabulary, and comprehension directly in your
                      classroom or after-school program.
                      <br />
                      <b>Why This Approach Works:</b> Each step builds on the
                      previous one, helping you gain foundational knowledge, see
                      effective strategies in practice, and implement them
                      confidently. Whether you’re a classroom teacher or
                      afterschool professional, these resources provide the tools
                      you need to create engaging, impactful literacy experiences
                      for K–2 learners.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <h1 className="classroom-resources-subpage-header-title">
              Vocabulary
            </h1>
            <p className="classroom-resources-subpage-header-description">
              Teach students how to develop a versatile vocabulary with
              resources that bring words to life through visual and interactive
              learning.
            </p>
          </div>
        </Container>
      </Section>
      <Section style={{ padding: '0' }}>
        <Container>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button classroom-resources-subpage-header-back-button--mobile"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className="classroom-resources-subpage-video">
            <Video guidSrc="8d83d6ac-69fe-4c05-829c-6f4afe714e62" />
            <p className="classroom-resources-subpage-video-category">
              Explainer video
            </p>
            <p className="classroom-resources-subpage-video-title">
              What is Vocabulary?
            </p>
            <p className="classroom-resources-subpage-video-description">
              Explore the crucial role of vocabulary in literacy instruction,
              highlighting how it shapes our ability to comprehend and use
              language effectively.
            </p>
          </div>
        </Container>
      </Section>
      <Section className="classroom-resources-subpage-resources classroom-resources-subpage-resources--vocabulary">
        <Container>
          <h2 className="classroom-resources-subpage-resources-title">
            Supporting Resources
          </h2>
          <Row>
            <Column size={4}>
              <Resource
                tophat={vocabularyTier1.tophat}
                tophatColor={vocabularyTier1.tophatColor}
                title={vocabularyTier1.title}
                duration={vocabularyTier1.duration}
                className={vocabularyTier1.className}
                subtitle={vocabularyTier1.subtitle}
                description={vocabularyTier1.description}
                img={vocabularyTier1.img}
                actions={vocabularyTier1.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={vocabularyTier2.tophat}
                tophatColor={vocabularyTier2.tophatColor}
                title={vocabularyTier2.title}
                duration={vocabularyTier2.duration}
                className={vocabularyTier2.className}
                subtitle={vocabularyTier2.subtitle}
                description={vocabularyTier2.description}
                img={vocabularyTier2.img}
                actions={vocabularyTier2.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={vocabularyTier3.tophat}
                tophatColor={vocabularyTier3.tophatColor}
                title={vocabularyTier3.title}
                duration={vocabularyTier3.duration}
                className={vocabularyTier3.className}
                subtitle={vocabularyTier3.subtitle}
                description={vocabularyTier3.description}
                img={vocabularyTier3.img}
                actions={vocabularyTier3.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card"
              size={12}
            >
              <Resource
                horizontal
                tophat={vocabularyMasterclass.tophat}
                tophatColor={vocabularyMasterclass.tophatColor}
                title={vocabularyMasterclass.title}
                duration={vocabularyMasterclass.duration}
                className={vocabularyMasterclass.className}
                subtitle={vocabularyMasterclass.subtitle}
                description={vocabularyMasterclass.description}
                img={vocabularyMasterclass.img}
                actions={vocabularyMasterclass.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card task-cards--vocabulary"
              size={12}
            >
              <Resource
                horizontal
                tophat={taskCards.tophat}
                tophatColor={taskCards.tophatColor}
                title={taskCards.title}
                duration={taskCards.duration}
                className={taskCards.className}
                subtitle={taskCards.subtitle}
                description={taskCards.description}
                img={taskCards.img}
                actions={taskCards.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column size={4}>
              <Resource
                tophat={vocabularyRtuTier1.tophat}
                tophatColor={vocabularyRtuTier1.tophatColor}
                title={vocabularyRtuTier1.title}
                duration={vocabularyRtuTier1.duration}
                className={vocabularyRtuTier1.className}
                subtitle={vocabularyRtuTier1.subtitle}
                description={vocabularyRtuTier1.description}
                img={vocabularyRtuTier1.img}
                actions={vocabularyRtuTier1.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={vocabularyRtuTier2.tophat}
                tophatColor={vocabularyRtuTier2.tophatColor}
                title={vocabularyRtuTier2.title}
                duration={vocabularyRtuTier2.duration}
                className={vocabularyRtuTier2.className}
                subtitle={vocabularyRtuTier2.subtitle}
                description={vocabularyRtuTier2.description}
                img={vocabularyRtuTier2.img}
                actions={vocabularyRtuTier2.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={vocabularyRtuTier3.tophat}
                tophatColor={vocabularyRtuTier3.tophatColor}
                title={vocabularyRtuTier3.title}
                duration={vocabularyRtuTier3.duration}
                className={vocabularyRtuTier3.className}
                subtitle={vocabularyRtuTier3.subtitle}
                description={vocabularyRtuTier3.description}
                img={vocabularyRtuTier3.img}
                actions={vocabularyRtuTier3.actions}
              />
            </Column>
          </Row>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query VocabularyResources {
    allResourcesJson(filter: { pages: { in: "classroom-resources" } }) {
      edges {
        node {
          slug
          tophat
          tophatColor
          img
          icon
          subject
          title
          subtitle
          duration
          description
          actions {
            label
            metadata
            module
            moduleName
            link
            download
            noIcon
            asLink
            videoGuid
          }
          horizontal
          className
          pages
        }
      }
    }
  }
`;

export default ClassroomResourcesPage;
